import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  CircleDot,
  Clock,
  GraduationCap,
  Heart,
  HeartPulse,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Scale,
  Shield,
  Sparkles,
  Stethoscope,
  Syringe,
  TrendingUp,
  UserRound,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/fibroadenoma-breast-lump-treatment-mumbai";

const FIBROADENOMA_FAQS = [
  {
    question: "Does a fibroadenoma always need to be removed?",
    answer:
      "No. Small, asymptomatic, confirmed simple fibroadenomas can be safely observed with regular ultrasound follow-up. Surgery is recommended when the fibroadenoma is large, growing, causing symptoms, creating cosmetic concern, or when the diagnosis is not entirely certain. Dr. Jain discusses the appropriate management for your specific lump during consultation.",
  },
  {
    question: "Can a fibroadenoma go away on its own?",
    answer:
      "Yes, particularly small ones. Fibroadenomas often stabilize in size and some reduce spontaneously, particularly after menopause when estrogen levels decline. This is one reason observation is a reasonable approach for small confirmed fibroadenomas rather than immediate surgical removal.",
  },
  {
    question: "Does fibroadenoma increase the risk of breast cancer?",
    answer:
      "Simple fibroadenoma does not carry a significant increased risk of breast cancer. Complex fibroadenoma carries a slightly elevated relative risk, though the absolute risk remains low. Regular follow-up is recommended for complex fibroadenomas. The presence of a fibroadenoma does not change standard breast cancer screening recommendations for age and risk profile.",
  },
  {
    question: "Can fibroadenoma come back after surgical removal?",
    answer:
      "The removed fibroadenoma does not recur from the same site. However, new fibroadenomas can develop elsewhere in the same breast or in the other breast, as the underlying hormonal predisposition remains. This is called a new fibroadenoma rather than a recurrence of the original.",
  },
  {
    question: "Is fibroadenoma surgery safe?",
    answer:
      "Yes. Fibroadenoma excision is a minor surgical procedure with a well-established safety profile. Risks include infection, bleeding, scarring, and in rare cases incomplete removal requiring re-excision. Choosing an experienced surgeon who places incisions carefully minimizes scarring and reduces complication rates.",
  },
  {
    question: "What is the difference between FNAC and core needle biopsy?",
    answer:
      "FNAC uses a fine needle to sample cells from the lump and provides rapid cytological information. Core needle biopsy uses a larger needle to take a tissue core for histological analysis, which gives more detailed information about tissue architecture. Core biopsy is more definitive and is used when FNAC findings are inconclusive or when a more complete tissue diagnosis is needed before making a management decision.",
  },
  {
    question: "Can fibroadenoma grow during pregnancy?",
    answer:
      "Yes. Fibroadenomas are influenced by estrogen and progesterone. They commonly enlarge during pregnancy and breastfeeding due to elevated hormone levels, then reduce in size afterward. Surgical removal during pregnancy is avoided unless the lump grows very rapidly or causes significant symptoms. Most pregnancy-related enlargement resolves after the hormonal change.",
  },
  {
    question: "Where is the incision made for fibroadenoma removal?",
    answer:
      "When anatomically feasible, the incision is placed at the edge of the areola where the scar fades most effectively against the natural color contrast between the areola and surrounding skin. For lumps in positions where a periareolar approach would require excessive dissection, the incision is placed directly over the lump in the most cosmetically acceptable orientation. Dr. Jain discusses scar placement during consultation based on your lump's location.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Breast fibroadenoma",
      alternateName: ["Fibroadenoma of the breast", "Benign breast fibroadenoma"],
      description:
        "A benign breast lump composed of glandular and fibrous tissue, common in young women; diagnosed by clinical examination, ultrasound, mammography when indicated, and FNAC or core biopsy; managed with observation or excision depending on size, growth, symptoms, and patient preference.",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Breast" },
      possibleTreatment: [
        "Observation with clinical examination and ultrasound follow-up",
        "Open surgical excision with histological analysis",
        "Vacuum-assisted excision under ultrasound guidance",
      ],
      signOrSymptom: [{ "@type": "MedicalSignOrSymptom", name: "Breast lump" }],
      url: PAGE_URL,
    },
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Fibroadenoma excision",
      description:
        "Daycare removal of a breast fibroadenoma through a periareolar or direct incision under local or general anesthesia; specimen sent for histology.",
      procedureType: "Surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Breast" },
      howPerformed:
        "Preoperative marking from imaging; incision at areolar edge or over lump; complete excision; layered cosmetic closure; 30–60 minutes; same-day discharge.",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: FIBROADENOMA_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function FibroadenomaJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Fibroadenoma", href: "/fibroadenoma-breast-lump-treatment-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Reconstructive Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Fibroadenoma of the Breast
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Diagnosis, Observation &amp; Surgical Removal by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Fibroadenoma" icon="🔬" size="hero"  imageSrc={HERO.fibroadenoma} imageAlt="Fibroadenoma treatment"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is a Fibroadenoma?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A fibroadenoma is a benign, non-cancerous breast lump composed of both glandular and fibrous breast tissue. It is one
              of the most common causes of a breast lump in young women, occurring most frequently between the ages of 15 and 35.
              Finding a breast lump is alarming for most patients. In the majority of cases, particularly in this age group, the
              cause is a fibroadenoma rather than something malignant.
            </p>
            <p>
              Fibroadenomas are not cancer and do not spread. Simple fibroadenomas carry no significant increased risk of breast
              cancer. They are influenced by estrogen, which is why they tend to appear during reproductive years, sometimes enlarge
              during pregnancy, and often shrink or disappear after menopause when estrogen levels fall.
            </p>
            <p>
              Dr. Vicky Jain assesses and manages fibroadenomas for patients who have found a breast lump and need proper
              diagnosis, monitoring, or surgical removal. Not every fibroadenoma requires surgery. The decision depends on size,
              symptoms, growth, and the patient&apos;s own concerns. Some are safely observed over time. Others are removed for size,
              cosmetic reasons, diagnostic certainty, or patient anxiety.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Fibroadenoma</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The first priority with any breast lump is accurate diagnosis. A fibroadenoma is a clinical and radiological diagnosis
                supported in some cases by tissue sampling. Dr. Jain does not recommend surgery on a breast lump without completing the
                appropriate investigations first. Ultrasound is the primary investigation in women under 35. Mammography is added for
                women over 35 or with additional risk factors. Fine needle aspiration cytology or core needle biopsy is used when
                imaging findings are not straightforward or when the diagnosis needs tissue confirmation before observation is an
                appropriate plan.
              </p>
              <p>
                Once the diagnosis is confirmed, Dr. Jain discusses the options honestly: observation with regular follow-up for small,
                asymptomatic fibroadenomas, or surgical removal for larger, symptomatic, growing, or cosmetically concerning lumps.
                Surgery is not the automatic recommendation for every fibroadenoma. Equally, observation is not offered when the
                size, growth pattern, or clinical picture makes removal the more appropriate plan. The recommendation is
                individualized to the specific lump and the specific patient.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TypesSection() {
  const cards = [
    {
      title: "Simple Fibroadenoma",
      icon: CircleDot,
      copy: "The most common type. Uniform structure on imaging, smooth and mobile on examination. Carries no significant increased risk of breast cancer. Most simple fibroadenomas under 2 to 3 centimeters in a patient with a confirmed diagnosis can be safely observed.",
    },
    {
      title: "Complex Fibroadenoma",
      icon: Stethoscope,
      copy: "Contains cysts, calcifications, or other internal changes on imaging. Carries a slightly elevated risk compared to simple fibroadenoma, though the absolute risk remains low. Core needle biopsy is typically recommended for complex fibroadenomas to confirm the diagnosis before deciding on management.",
    },
    {
      title: "Giant Fibroadenoma",
      icon: Scale,
      copy: "Defined as larger than 5 centimeters. Can cause visible breast enlargement, asymmetry, and skin stretching. Surgical removal is almost always recommended for giant fibroadenomas regardless of symptoms due to size and the distortion they cause.",
    },
    {
      title: "Juvenile Fibroadenoma",
      icon: Heart,
      copy: "Occurs in adolescent girls. Often grows rapidly. Surgical removal is frequently recommended to prevent breast distortion during development, but the timing and approach are planned carefully to minimise impact on normal breast tissue.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Types of Fibroadenoma</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DiagnosisSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="diagnosis-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Fibroadenoma is Diagnosed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Clinical examination alone is not sufficient to confirm a fibroadenoma diagnosis. A smooth, mobile, rubbery lump in a
              young woman is highly suggestive, but the same presentation can occur in other benign conditions and occasionally in
              malignancy. Investigations are required before any management decision is made.
            </p>
            <p>
              Ultrasound is the first-line investigation for women under 35. It distinguishes solid lumps like fibroadenomas from
              cysts, assesses size and margins, and guides biopsy when needed. Mammography is added for women over 35 or with
              additional risk factors such as a strong family history. Fine needle aspiration cytology samples cells from the lump
              using a fine needle and provides a rapid cytological diagnosis. Core needle biopsy takes a tissue sample for histological
              analysis and is more definitive in cases where FNAC findings are inconclusive.
            </p>
            <p>
              The combination of clinical examination, imaging, and tissue sampling when indicated is called triple assessment. This is
              the standard approach to any breast lump and is what Dr. Jain follows before recommending observation or surgery.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhenTreatmentSection() {
  const cards = [
    {
      title: "Size Above 3 to 4 Centimetres",
      icon: Scale,
      copy: "Larger fibroadenomas carry a greater risk of continued growth and cause more significant breast distortion. Surgical removal is generally recommended once a fibroadenoma exceeds 3 to 4 centimeters regardless of symptoms.",
    },
    {
      title: "Increasing Size on Follow-Up",
      icon: TrendingUp,
      copy: "A fibroadenoma that is growing on serial ultrasound scans warrants removal even if it has not yet reached a large size. Growth changes the risk-benefit calculation for observation versus excision.",
    },
    {
      title: "Pain or Discomfort",
      icon: HeartPulse,
      copy: "Most fibroadenomas are painless. When pain is consistently associated with the lump rather than with the menstrual cycle generally, removal is a reasonable option.",
    },
    {
      title: "Cosmetic Concern or Asymmetry",
      icon: UserRound,
      copy: "A fibroadenoma that is large enough to cause visible breast asymmetry or distortion is a legitimate indication for surgical removal on aesthetic grounds.",
    },
    {
      title: "Diagnostic Uncertainty",
      icon: Syringe,
      copy: "When imaging and cytology findings are not entirely consistent with a benign fibroadenoma, surgical excision and histological analysis of the entire lump provides definitive diagnosis.",
    },
    {
      title: "Patient Anxiety",
      icon: Shield,
      copy: "Persistent anxiety about a confirmed benign lump is a valid reason for elective removal. Living with ongoing worry about a lump that can be safely removed is not a requirement of the observation approach.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="when-treatment-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            When Does a Fibroadenoma Need to Be Removed?
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TREATMENT_OPTIONS = [
  {
    title: "Observation (Watchful Waiting)",
    body:
      "For small, asymptomatic, confirmed simple fibroadenomas, observation with clinical examination and ultrasound at 6 to 12 month intervals is an entirely appropriate management plan. Many fibroadenomas remain stable in size for years. Some reduce in size spontaneously, particularly after menopause. Observation does not mean ignoring the lump. It means monitoring it with a clear plan for what would change the recommendation to surgical removal.",
  },
  {
    title: "Surgical Excision",
    body:
      "The definitive treatment. The fibroadenoma is removed completely through a small incision placed near the areola or directly over the lump depending on its location. The specimen is sent for histological analysis to confirm the diagnosis. Surgery is a daycare procedure taking 30 to 60 minutes under local or general anesthesia. Recovery is straightforward for most patients.",
  },
  {
    title: "Vacuum-Assisted Excision",
    body:
      "A minimally invasive technique using a probe inserted through a small skin incision under ultrasound guidance to remove the fibroadenoma without an open surgical incision. Appropriate for smaller fibroadenomas in suitable locations. Produces minimal scarring. Not available for all fibroadenoma sizes and positions.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Fibroadenoma Treatment Options</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {TREATMENT_OPTIONS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Fibroadenoma Removal Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 30 to 60 minutes and is performed as a daycare procedure under local or general anesthesia depending on
              the size of the fibroadenoma and patient preference. Dr. Jain marks the lump position using the preoperative ultrasound
              report before surgery.
            </p>
            <p>
              A small incision is made near the areola when anatomically feasible, as the periareolar scar fades better than incisions
              placed directly over a lump elsewhere on the breast. For lumps in positions where a periareolar incision would require
              extensive dissection, the incision is placed directly over the lump in the least conspicuous orientation. The
              fibroadenoma is removed completely and sent for histological analysis. The wound is closed with cosmetic sutures.
            </p>
            <p>
              Most patients go home the same day. The breast is supported with a well-fitting bra. Wound care is straightforward.
              Histology results are reviewed at the post-operative visit, typically within 1 to 2 weeks.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild pain and swelling at the incision site. Normal and expected. Managed with standard pain medication. A supportive bra reduces discomfort.",
      icon: BedDouble,
    },
    {
      period: "Days 5 to 7",
      description:
        "Most patients resume normal daily activity including desk work. The incision is clean and healing. Sutures dissolve or are reviewed at the post-operative visit.",
      icon: Monitor,
    },
    {
      period: "Week 2",
      description:
        "Complete healing for most patients. Histology results are reviewed and discussed. Strenuous exercise resumes after 2 weeks.",
      icon: Clock,
    },
    {
      period: "Months 1 to 6",
      description:
        "The incision scar matures and fades. Periareolar scars in particular become difficult to see within 6 months. No ongoing restriction on activity.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_CANCER_ROWS = [
  {
    label: "Nature",
    fibro:
      "Benign. Does not invade surrounding tissue. Does not spread.",
    cancer: "Malignant. Invades surrounding tissue and can spread to lymph nodes and other organs.",
  },
  {
    label: "Feel on Examination",
    fibro: "Smooth, round or oval, firm but rubbery, moves freely under the skin.",
    cancer: "Often irregular in shape, may feel hard, may be fixed to surrounding tissue.",
  },
  {
    label: "Growth",
    fibro: "Slow or stable. May fluctuate with hormonal cycle.",
    cancer: "Often progressive growth.",
  },
  {
    label: "Skin or Nipple Changes",
    fibro: "Not associated with skin dimpling or nipple changes.",
    cancer: "May cause skin dimpling, nipple retraction, or nipple discharge.",
  },
  {
    label: "Age Group",
    fibro: "Most common in women aged 15 to 35.",
    cancer: "Risk increases with age, most common over 40.",
  },
];

function FibroadenomaVsCancerSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="fibroadenoma-vs-cancer-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            How Fibroadenoma Differs from Breast Cancer
          </h2>
        </div>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Fibroadenoma
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Breast Cancer
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_CANCER_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.fibro}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.cancer}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed">
          Clinical features alone cannot definitively distinguish a fibroadenoma from a malignant lump. Proper investigations are
          always required. Any new breast lump should be assessed by a qualified doctor regardless of how benign it feels on
          self-examination.
        </p>
      </div>
    </section>
  );
}

function WhoShouldSeeSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-see-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            When to Seek Assessment for a Breast Lump
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any woman who has found a new breast lump should seek assessment, regardless of age, regardless of whether it is
              painless, and regardless of how smooth and mobile it feels. The majority of breast lumps in women under 35 are benign,
              but the only way to confirm this is through proper clinical and radiological assessment. Self-diagnosis based on feel is
              not reliable.
            </p>
            <p>
              Seek assessment promptly if the lump is increasing in size, if there is associated skin change, nipple discharge or
              retraction, pain that is not cycle-related, or a strong family history of breast cancer. These features do not confirm
              malignancy but they make investigation more urgent. For a lump that has been present for some time and already confirmed
              as a fibroadenoma on previous investigations, the question of whether to continue observation or proceed to removal is a
              decision Dr. Jain can help make based on current size, growth pattern, and the patient&apos;s own priorities.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Breast" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {FIBROADENOMA_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`fib-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book a Breast Lump Assessment</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          If you have found a breast lump or have a known fibroadenoma and want a second opinion on management, Dr. Jain provides a
          thorough clinical assessment, arranges appropriate investigations, and gives you a clear, honest recommendation on whether
          observation or removal is the right approach for your specific situation.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Breast Augmentation",
      description: "Breast enhancement for patients who have had fibroadenoma removal and want to address volume or symmetry.",
      href: "/breast-implant-surgery-mumbai",
    },
    {
      name: "Breast Lift",
      description: "Correct breast position and shape after multiple lump removals or significant breast changes.",
      href: "/breast-lift-surgery-mumbai",
    },
    {
      name: "Breast Implant Removal",
      description: "For patients managing breast health concerns across multiple procedures.",
      href: "/breast-implant-removal-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function FibroadenomaPage() {
  return (
    <>
      <SEO
        title="Fibroadenoma Breast Lump Treatment | Dr. Vicky Jain"
        description="Found a breast lump? Fibroadenoma is benign but needs proper diagnosis. Dr. Vicky Jain diagnoses and removes fibroadenomas. Book your consultation."
        canonical="/fibroadenoma-breast-lump-treatment-mumbai"
      />
      <FibroadenomaJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <TypesSection />
      <DiagnosisSection />
      <WhenTreatmentSection />
      <TreatmentOptionsSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <FibroadenomaVsCancerSection />
      <WhoShouldSeeSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
